import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

/**
 * 🎨 THEME CONTEXT - Профессиональная система управления темами v2.0
 * 
 * Архитектура:
 * - Минимальная логика в JS - только управление data-theme атрибутом
 * - Все стили и цвета определены в CSS (tokens.css, themes.css)
 * - Сохранение выбора в localStorage с валидацией
 * - Автоопределение системной темы (prefers-color-scheme)
 * - Плавные переходы между темами
 * - Типобезопасность через TypeScript
 * - Callback для отслеживания изменений темы
 */

// ============================================
// TYPES & INTERFACES
// ============================================

/** Доступные темы приложения */
export type ThemeName = 'white' | 'elegant-dark' | 'indigo';

/** Специальное значение для автоопределения темы из системы */
export type ThemeMode = ThemeName | 'system';

/** Категория темы для группировки */
export type ThemeCategory = 'light' | 'dark';

/** Конфигурация темы */
export interface ThemeConfig {
  name: ThemeName;
  label: string;
  description: string;
  icon: string;
  category: ThemeCategory;
  /** Порядок отображения в списке */
  order: number;
}

/** Контекст темы */
interface ThemeContextType {
  /** Текущая применённая тема */
  currentTheme: ThemeName;
  /** Текущий режим (может быть 'system') */
  currentMode: ThemeMode;
  /** Установить тему или режим */
  setTheme: (theme: ThemeMode) => void;
  /** Доступные темы */
  availableThemes: ThemeConfig[];
  /** Системная тема (определённая из prefers-color-scheme) */
  systemTheme: ThemeName;
  /** Переходит ли сейчас между темами */
  isTransitioning: boolean;
}

// ============================================
// CONSTANTS
// ============================================

/** Ключ для localStorage */
const STORAGE_KEY = 'ryaskov-pro-theme-mode';

/** Тема по умолчанию */
const DEFAULT_THEME: ThemeName = 'white';

/** Длительность анимации перехода темы (мс) */
const TRANSITION_DURATION = 300;

/** Конфигурация всех доступных тем */
export const AVAILABLE_THEMES: ThemeConfig[] = [
  {
    name: 'white',
    label: 'Белая',
    description: 'Чистая белая тема с максимальной читаемостью',
    icon: 'ri-layout-line',
    category: 'light',
    order: 1,
  },
  {
    name: 'elegant-dark',
    label: 'Серая',
    description: 'Нейтральная серая тема',
    icon: 'ri-contrast-line',
    category: 'dark',
    order: 2,
  },
  {
    name: 'indigo',
    label: 'Индиго',
    description: 'Сдержанная тёмная тема с лёгким серо-индиго',
    icon: 'ri-palette-line',
    category: 'dark',
    order: 3,
  },
];

// ============================================
// HELPERS
// ============================================

/**
 * Определяет системную тему из медиа-запроса
 */
const getSystemTheme = (): ThemeName => {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  
  const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  return isDark ? 'elegant-dark' : 'white';
};

/**
 * Валидация имени темы
 */
const isValidTheme = (theme: string): theme is ThemeName => {
  return AVAILABLE_THEMES.some(t => t.name === theme);
};

/**
 * Валидация режима темы
 */
const isValidMode = (mode: string): mode is ThemeMode => {
  return mode === 'system' || isValidTheme(mode);
};

/**
 * Загружает сохранённый режим из localStorage
 */
const loadStoredMode = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isValidMode(stored)) {
      return stored as ThemeMode;
    }
  } catch (error) {
    console.warn('[ThemeContext] Failed to load theme from localStorage:', error);
  }
  return DEFAULT_THEME;
};

/**
 * Сохраняет режим в localStorage
 */
const saveMode = (mode: ThemeMode): void => {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch (error) {
    console.warn('[ThemeContext] Failed to save theme to localStorage:', error);
  }
};

/**
 * Применяет тему к DOM
 */
const applyThemeToDOM = (theme: ThemeName): void => {
  // Устанавливаем data-theme атрибут на documentElement (html)
  document.documentElement.setAttribute('data-theme', theme);
  
  // Также добавляем класс на body для обратной совместимости
  document.body.className = `theme-${theme}`;
  
  // Добавляем мета-тег для мобильных браузеров
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    const themeColors: Record<ThemeName, string> = {
      'white': '#ffffff',
      'elegant-dark': '#1a1a1a',
      'indigo': '#000000',
    };
    metaThemeColor.setAttribute('content', themeColors[theme]);
  }
};

// ============================================
// CONTEXT
// ============================================

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  /** Callback при изменении темы */
  onThemeChange?: (theme: ThemeName) => void;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ 
  children,
  onThemeChange,
}) => {
  // Состояния
  const [systemTheme, setSystemTheme] = useState<ThemeName>(getSystemTheme);
  const [currentMode, setCurrentMode] = useState<ThemeMode>(loadStoredMode);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Вычисляем текущую тему на основе режима
  const currentTheme: ThemeName = currentMode === 'system' ? systemTheme : currentMode;

  // Отслеживаем изменения системной темы
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      const newSystemTheme = e.matches ? 'elegant-dark' : 'white';
      setSystemTheme(newSystemTheme);
    };

    // Современные браузеры
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } 
    // Старые браузеры
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Применяем тему при изменении
  useEffect(() => {
    // Включаем режим перехода
    setIsTransitioning(true);
    document.documentElement.classList.add('theme-transitioning');

    // Применяем тему
    applyThemeToDOM(currentTheme);

    // Вызываем callback
    onThemeChange?.(currentTheme);

    // Выключаем режим перехода через указанное время
    const timer = setTimeout(() => {
      setIsTransitioning(false);
      document.documentElement.classList.remove('theme-transitioning');
    }, TRANSITION_DURATION);

    return () => clearTimeout(timer);
  }, [currentTheme, onThemeChange]);

  // Сохраняем режим в localStorage при изменении
  useEffect(() => {
    saveMode(currentMode);
  }, [currentMode]);

  // Функция изменения темы
  const setTheme = useCallback((mode: ThemeMode) => {
    if (!isValidMode(mode)) {
      console.warn(`[ThemeContext] Invalid theme mode: ${mode}`);
      return;
    }
    setCurrentMode(mode);
  }, []);

  const value: ThemeContextType = {
    currentTheme,
    currentMode,
    setTheme,
    availableThemes: AVAILABLE_THEMES,
    systemTheme,
    isTransitioning,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// ============================================
// HOOK
// ============================================

/**
 * Хук для использования темы в компонентах
 * @throws {Error} Если используется вне ThemeProvider
 */
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Получить конфигурацию темы по имени
 */
export const getThemeConfig = (theme: ThemeName): ThemeConfig | undefined => {
  return AVAILABLE_THEMES.find(t => t.name === theme);
};

/**
 * Получить темы по категории
 */
export const getThemesByCategory = (category: ThemeCategory): ThemeConfig[] => {
  return AVAILABLE_THEMES
    .filter(t => t.category === category)
    .sort((a, b) => a.order - b.order);
};

/**
 * Проверить, является ли тема тёмной
 */
export const isDarkTheme = (theme: ThemeName): boolean => {
  const config = getThemeConfig(theme);
  return config?.category === 'dark';
};

/**
 * Проверить, является ли тема светлой
 */
export const isLightTheme = (theme: ThemeName): boolean => {
  const config = getThemeConfig(theme);
  return config?.category === 'light';
};
